<template>
  <label :class="['full-width', labelClass]" @click="noOp">
    <div v-if="!noLabel && (inputLabel || hintLabel)">
      <span v-if="inputLabel" class="date-picker-label">{{ inputLabel }}</span>
      <span v-if="hintLabel" style="color: #626262"
        ><em>{{ hintLabel }}</em></span
      >
    </div>
    <div class="d-flex">
      <div
        v-if="buttons"
        class="arrow-cont"
        data-cy="previous-day-btn"
        @click="prevDay()"
      >
        <i class="far fa-chevron-left"></i>
      </div>
      <date-picker
        :key="pickerMode"
        v-model:value="date"
        v-bind="$attrs"
        :format="format"
        :value-type="valueType"
        :placeholder="placeholderLabel"
        class="full-width"
        :range="range"
        :input-class="inputClassName"
        :popup-class="popupClasses"
        :editable="isSheet ? false : editable"
        :disabled="disabled"
        :input-attr="inputAttr"
        :open="isSheet ? false : undefined"
        @change="checkClearingData"
        @blur="$emit('blur')"
        @pick="($evt) => $emit('pick', $evt)"
        @update:open="onRequestOpen"
      >
        <template
          v-if="range && shortcuts && periods?.length"
          #footer="{ emit }"
        >
          <Shortcuts :emit="emit" :periods="periods" />
        </template>
        <template #header><slot name="header"></slot></template>
      </date-picker>

      <DatepickerSheet
        v-if="sheetOpen"
        :model-value="sheetValue"
        :range="range"
        :periods="sheetPeriods"
        :title="inputLabel || placeholderLabel"
        :format="format"
        @update:model-value="onSheetApply"
        @close="sheetOpen = false"
      />
      <div
        v-if="buttons"
        class="arrow-cont"
        data-cy="next-day-btn"
        @click="nextDay()"
      >
        <i class="far fa-chevron-right"></i>
      </div>
    </div>
    <span
      v-if="findByName(validationErrors, name) || fieldErrorMessage"
      class="text-danger error-text"
      >{{ findByName(validationErrors, name) || fieldErrorMessage }}</span
    >
  </label>
</template>

<script>
import moment from "moment";
import DatePicker from "vue-datepicker-next";
import { findByName } from "@/lib/vuelidate_helpers";

import {
  DatepickerSheetAsync,
  useDatepickerSheet
} from "@/components/Seranova/Datepicker/useDatepickerSheet";
import CONSTANTS from "@/Constants.js";
import Shortcuts from "@/Reports/Shortcuts.vue";
import useVuelidate from "@vuelidate/core";
import { requiredIf } from "@vuelidate/validators";
import "vue-datepicker-next/index.css";

export default {
  components: {
    "date-picker": DatePicker,
    DatepickerSheet: DatepickerSheetAsync,
    Shortcuts
  },
  props: {
    format: {
      type: String,
      default: CONSTANTS.DATE_FORMAT
    },
    // Days moved by the prev/next buttons (e.g. 7 for a week-stepping picker)
    step: {
      type: Number,
      default: 1
    },
    labelClass: {
      type: String,
      default: ""
    },
    popupClass: {
      type: String,
      default: ""
    },
    name: {
      type: String,
      default: ""
    },
    noLabel: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: ""
    },
    periods: {
      type: Array,
      default: null
    },
    hintLabel: {
      type: String,
      default: ""
    },
    modelValue: {
      type: [String, Date, Array],
      default: null
    },
    valueType: {
      type: String,
      default: "format"
    },
    validationErrors: {
      type: Object,
      default() {
        return {
          first: () => {}
        };
      }
    },
    fieldError: {
      type: Boolean,
      default: false
    },
    fieldErrorMessage: {
      type: String,
      default: ""
    },
    range: {
      type: Boolean,
      default: false
    },
    shortcuts: {
      type: Boolean,
      default: false
    },
    buttons: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      default: ""
    },
    editable: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    inputAttr: {
      type: Object,
      default: () => ({})
    },
    isRequired: {
      type: Boolean,
      default: false
    },
    // Opt-in, because this picker has 17 other call sites whose desktop
    // presentation must not change. Only the report and datatable filters
    // (via DynamicInput) ask for the phone sheet.
    mobileSheet: {
      type: Boolean,
      default: false
    }
  },
  emits: ["update:modelValue", "pick", "clear-date", "blur"],
  setup(props, { emit }) {
    // Lives here rather than in `methods` so the sheet's apply path and the
    // v-model setter share one definition; `computed.date` still reaches it as
    // `this.parseDate`.
    const parseDate = (val) =>
      val ? moment(val, props.format).toISOString() : val;

    const sheet = useDatepickerSheet(props, {
      setDate: (value) =>
        emit(
          "update:modelValue",
          Array.isArray(value) ? value.map(parseDate) : parseDate(value)
        ),
      clearDate: () => emit("clear-date")
    });

    return {
      v$: useVuelidate(),
      parseDate,
      ...sheet
    };
  },
  computed: {
    date: {
      get() {
        let date = null;
        if (Array.isArray(this.modelValue)) {
          date = this.modelValue.map((date) => this.formatDate(date));
        } else {
          date = this.formatDate(this.modelValue);
        }
        return date;
      },
      set(val) {
        let date = null;
        if (Array.isArray(val)) {
          date = val.map((date) => this.parseDate(date));
        } else {
          date = this.parseDate(val);
        }
        this.$emit("update:modelValue", date);
      }
    },
    // The sheet parses what it is given with `format`, so it has to be handed
    // the DISPLAYED value, not the raw model value. The two are not the same
    // shape: DynamicInput hands the report filters DATE_API_FORMAT strings
    // ("09/22/2026"), and parsing a four-digit year with the display format
    // MM/DD/YY reads the year as 20 and drops the "26" -- the sheet opened on
    // Oct 2020 for a filter set to Oct 2026. `date` is already normalised to
    // `format` by formatDate, which knows about both shapes.
    sheetValue() {
      const value = this.modelValue;
      const empty = Array.isArray(value) ? !value.length : !value;
      return empty ? null : this.date;
    },
    popupClasses() {
      return [this.popupClass, this.range ? "date-ranger-picker" : ""]
        .filter(Boolean)
        .join(" ");
    },
    inputLabel() {
      return this.label || this.name || this.placeholder;
    },
    inputClassName() {
      const errorFound = findByName(this.validationErrors, this.name);
      const hasError = errorFound || this.fieldError || this.fieldErrorMessage;
      const squared = this.buttons;
      return `form-control date-picker-input full-width ${
        hasError && "is-invalid"
      } ${squared && "squared"}`;
    },
    placeholderLabel() {
      return this.placeholder || this.name;
    }
  },
  validations() {
    return {
      form: {
        check_number: {
          requiredIf: requiredIf(this.isRequired),
          $autoDirty: true
        }
      }
    };
  },
  methods: {
    noOp(e) {
      // Prevent label click from opening calendar
      // Calendar should only open on input click
      e.preventDefault();
    },
    // TODO: We should have the datepicker only support ISO strings moving forward
    formatDate(val) {
      if (moment(val, moment.ISO_8601).isValid()) {
        return moment(val).format(this.format);
      }
      return moment(val, "MM/DD/YYYY").format(this.format);
    },
    checkClearingData(date) {
      if (!date || (!date[0] && !date[1])) {
        this.$emit("clear-date");
      }
    },
    prevDay() {
      this.date = moment(this.date, this.format)
        .subtract(this.step, "days")
        .format(this.format);
    },
    nextDay() {
      this.date = moment(this.date, this.format)
        .add(this.step, "days")
        .format(this.format);
    },
    findByName
  }
};
</script>

<style lang="scss">
.date-ranger-picker.mx-datepicker-main {
  .mx-datepicker-content {
    display: flex;
    .mx-calendar-range {
      order: 2;
    }
    .mx-datepicker-footer {
      order: 1;
      border-right: 1px solid #e8e8e8;
      .shortcuts {
        flex-direction: column;
      }
    }
  }
}
.mx-datepicker {
  /* The library ships this wrapper as inline-block with a fixed 210px width,
     while the input inside it carries `full-width` (100% of the label). In a
     narrow table column -- which is every report's column filter -- the two
     diverge, and two things break:

       - the calendar icon is absolutely positioned against the WRAPPER's right
         edge, so it lands INSIDE the input, floating over the date text;
       - the part of the input past the wrapper is covered by the neighbouring
         column's <th>, so a tap there never reaches the input at all.

     On a mouse that is mostly cosmetic. On touch it means the first tap lands
     on the icon or the neighbouring header and only the second reaches the
     field, which is why the mobile sheet appeared to need two taps to open.
     CalendarDatepicker carries the same reset for the same reason. */
  display: block;
  width: 100%;

  .form-control[readonly] {
    background-color: #fff !important;
  }
}
/* A touch device resolves :hover on the first tap and only delivers the click
   on the second, so any hover-only affordance costs a tap. The library reveals
   its clear icon on `.mx-input-wrapper:hover` and hides the calendar icon in
   the same breath -- that swap is what the first tap was being spent on, which
   is why the mobile sheet appeared to need two taps to open. Where there is no
   hover, keep both icons static so the first tap is a click.

   Losing the hover-to-clear affordance on touch costs nothing: the sheet has
   its own Clear button, and it was never discoverable by tapping anyway. */
@media (hover: none) {
  .mx-input-wrapper:hover .mx-icon-clear {
    display: none;
  }

  .mx-input-wrapper:hover .mx-icon-clear + .mx-icon-calendar {
    display: block;
  }
}

.mx-calendar-content .cell.active {
  background-color: var(--primary-color);
}
.mx-icon-calendar {
  right: 6px;
}
.date-picker-label {
  font-weight: bold;
  margin-left: 2px;
  margin-bottom: 3px;
  display: inline-block;
  min-height: 15px;
}
.date-picker-input {
  padding-right: 20px;
}
.arrow-cont {
  background-color: #fff;
  padding: 10px 15px;
  box-shadow: var(--box-shadow);
  height: 36px;
  border: 1.5px solid var(--sera-color-fields-border, #9ca4aa);
  color: var(--primary-color);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    background-color: var(--primary-color);
    color: #fff;
  }
  &:first-of-type {
    border-radius: 3px 0 0 3px;
  }

  &:last-of-type {
    border-radius: 0 3px 3px 0;
  }
}
.squared {
  border-radius: 0 !important;
  border-left: none !important;
  border-right: none !important;
}
</style>
